// src/features/admin/components/settings/integrations/components/EFilingTab.tsx
import React from 'react';
import {
  DocumentDuplicateIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const EFILING_PROVIDERS = [
  {
    name: 'Tyler Odyssey eFileManager',
    description: 'Comprehensive e-filing solution integrated with Tyler court systems.',
    coverage: '38+ states with Odyssey courts',
    features: ['Auto-fill forms', 'Fee calculation', 'Status tracking', 'Rejection handling']
  },
  {
    name: 'File & ServeXpress',
    description: 'E-filing and electronic service of process in one platform.',
    coverage: 'Federal and state courts',
    features: ['E-filing', 'E-service', 'Document delivery', 'Compliance verification']
  },
  {
    name: 'LegalConnect',
    description: 'Document assembly and e-filing with court-specific formatting.',
    coverage: 'Multiple state courts',
    features: ['Document assembly', 'Court forms', 'Auto-formatting', 'Batch filing']
  },
  {
    name: 'InfoTrack',
    description: 'International e-filing with support for US, UK, and Australian courts.',
    coverage: 'International',
    features: ['Multi-jurisdiction', 'Due diligence', 'Court searches', 'Filing automation']
  },
  {
    name: 'TurboCourt',
    description: 'Self-service e-filing for state courts with guided interviews.',
    coverage: 'State courts',
    features: ['Guided filing', 'Form wizard', 'Fee payment', 'Status updates']
  },
  {
    name: 'One Legal',
    description: 'California-focused e-filing and court services.',
    coverage: 'California courts',
    features: ['CA e-filing', 'Court runners', 'Research', 'Process service']
  }
];

const EFilingTab: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Coming Soon Banner */}
      <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-6 text-center">
        <ClockIcon className="h-12 w-12 text-amber-600 dark:text-amber-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200 mb-2">
          E-Filing Integration - Coming in Phase 3
        </h3>
        <p className="text-sm text-amber-700 dark:text-amber-300 max-w-2xl mx-auto">
          File court documents directly from EdgeLex with automatic formatting, fee calculation, and status tracking.
          This feature is scheduled for Phase 3 of development (Weeks 19-26).
        </p>
      </div>

      {/* Preview */}
      <div>
        <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <DocumentDuplicateIcon className="h-5 w-5 mr-2 text-lex-accent" />
          Planned E-Filing Integrations
        </h4>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {EFILING_PROVIDERS.map((provider, index) => (
            <div
              key={index}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 opacity-75"
            >
              <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                {provider.name}
              </h5>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                {provider.description}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                <strong>Coverage:</strong> {provider.coverage}
              </p>
              <div className="flex flex-wrap gap-1">
                {provider.features.map((feature, idx) => (
                  <span
                    key={idx}
                    className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Workflow Preview */}
      <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
          E-Filing Workflow Preview
        </h5>
        <div className="flex items-center justify-between text-xs">
          <div className="text-center">
            <div className="w-8 h-8 bg-lex-accent-soft rounded-full flex items-center justify-center mx-auto mb-1">
              <span className="text-lex-accent font-semibold">1</span>
            </div>
            <span className="text-gray-600 dark:text-gray-400">Create Document</span>
          </div>
          <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600 mx-2"></div>
          <div className="text-center">
            <div className="w-8 h-8 bg-lex-accent-soft rounded-full flex items-center justify-center mx-auto mb-1">
              <span className="text-lex-accent font-semibold">2</span>
            </div>
            <span className="text-gray-600 dark:text-gray-400">Select Court</span>
          </div>
          <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600 mx-2"></div>
          <div className="text-center">
            <div className="w-8 h-8 bg-lex-accent-soft rounded-full flex items-center justify-center mx-auto mb-1">
              <span className="text-lex-accent font-semibold">3</span>
            </div>
            <span className="text-gray-600 dark:text-gray-400">Auto-Format</span>
          </div>
          <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600 mx-2"></div>
          <div className="text-center">
            <div className="w-8 h-8 bg-lex-accent-soft rounded-full flex items-center justify-center mx-auto mb-1">
              <span className="text-lex-accent font-semibold">4</span>
            </div>
            <span className="text-gray-600 dark:text-gray-400">E-File</span>
          </div>
          <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600 mx-2"></div>
          <div className="text-center">
            <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-1">
              <span className="text-green-600 font-semibold">✓</span>
            </div>
            <span className="text-gray-600 dark:text-gray-400">Confirmation</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EFilingTab;
